import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import {
  unwrapMasterKey,
  generateMasterKey,
  encrypt,
  AuthClient,
} from "@gbougakov/brief-ts";
import type { AuthSuccessResponse } from "@gbougakov/brief-ts";
import { authenticate, register, authClient } from "@/lib/auth";
import { saveCredentials, loadCredentials, clearCredentials } from "@/lib/key-store";
import { BRIEF_ENDPOINT } from "@/lib/constants";

export type AuthState =
  | "loading"
  | "unauthenticated"
  | "needs-passphrase"
  | "needs-setup"
  | "authenticated";

interface AuthContextValue {
  state: AuthState;
  sessionToken: string | null;
  masterKey: CryptoKey | null;
  error: string | null;
  startAuth: () => Promise<void>;
  submitPassphrase: (passphrase: string) => Promise<void>;
  setupPassphrase: (passphrase: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export { AuthContext };

export function useAuthState() {
  const [state, setState] = useState<AuthState>("loading");
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [masterKey, setMasterKey] = useState<CryptoKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingAuthResponse, setPendingAuthResponse] =
    useState<AuthSuccessResponse | null>(null);

  // Try loading saved credentials on mount
  useEffect(() => {
    loadCredentials()
      .then((creds) => {
        if (creds) {
          setSessionToken(creds.sessionToken);
          setMasterKey(creds.masterKey);
          setState("authenticated");
        } else {
          setState("unauthenticated");
        }
      })
      .catch(() => {
        setState("unauthenticated");
      });
  }, []);

  const startAuth = useCallback(async () => {
    setError(null);
    try {
      let result: AuthSuccessResponse;
      try {
        result = await authenticate();
      } catch {
        result = await register();
      }

      setSessionToken(result.sessionToken);
      setPendingAuthResponse(result);

      if (result.hasWrappedKey) {
        setState("needs-passphrase");
      } else {
        setState("needs-setup");
      }
    } catch (e: any) {
      setError(e.message || "Authentication failed");
    }
  }, []);

  const submitPassphrase = useCallback(
    async (passphrase: string) => {
      setError(null);
      if (!pendingAuthResponse) return;
      try {
        const wrapped = await authClient.getWrappedKey(
          pendingAuthResponse.sessionToken,
        );
        const key = await unwrapMasterKey(
          wrapped.wrappedKey,
          wrapped.salt,
          wrapped.iterations,
          passphrase,
        );
        await saveCredentials(pendingAuthResponse.sessionToken, key);
        setMasterKey(key);
        setState("authenticated");
      } catch (e: any) {
        setError("Incorrect passphrase. Please try again.");
      }
    },
    [pendingAuthResponse],
  );

  const setupPassphrase = useCallback(
    async (passphrase: string) => {
      setError(null);
      if (!pendingAuthResponse) return;
      try {
        const { raw, cryptoKey } = await generateMasterKey();

        // Wrap the master key with the passphrase
        const salt = crypto.getRandomValues(new Uint8Array(16));
        const iterations = 600000;

        // Derive wrapping key and encrypt the raw master key
        const encoder = new TextEncoder();
        const keyMaterial = await crypto.subtle.importKey(
          "raw",
          encoder.encode(passphrase),
          { name: "PBKDF2" },
          false,
          ["deriveBits"],
        );
        const derivedBits = await crypto.subtle.deriveBits(
          { name: "PBKDF2", salt, iterations, hash: "SHA-256" },
          keyMaterial,
          256,
        );
        const wrappingKey = await crypto.subtle.importKey(
          "raw",
          derivedBits,
          { name: "AES-GCM" },
          false,
          ["encrypt", "decrypt"],
        );

        const wrappedKeyB64 = await encrypt(raw, wrappingKey);

        // Base64 encode the salt
        let saltB64: string;
        if (typeof Buffer !== "undefined") {
          saltB64 = Buffer.from(salt).toString("base64");
        } else {
          let binary = "";
          for (const b of salt) binary += String.fromCharCode(b);
          saltB64 = btoa(binary);
        }

        await authClient.storeWrappedKey(pendingAuthResponse.sessionToken, {
          wrappedKey: wrappedKeyB64,
          salt: saltB64,
          iterations,
        });

        await saveCredentials(pendingAuthResponse.sessionToken, cryptoKey);
        setMasterKey(cryptoKey);
        setState("authenticated");
      } catch (e: any) {
        setError(e.message || "Failed to set up recovery passphrase");
      }
    },
    [pendingAuthResponse],
  );

  const logout = useCallback(async () => {
    await clearCredentials();
    setSessionToken(null);
    setMasterKey(null);
    setPendingAuthResponse(null);
    setState("unauthenticated");
  }, []);

  return {
    state,
    sessionToken,
    masterKey,
    error,
    startAuth,
    submitPassphrase,
    setupPassphrase,
    logout,
  };
}
